import { useNavigate } from 'react-router-dom';
import { MessageSquare, Inbox } from 'lucide-react';
import useAICoachStore from '../../stores/useAICoachStore';
import useInboxStore from '../../stores/useInboxStore';

/**
 * HeaderActions — the two ambient utility entry points (Inbox, AI Coach quick
 * widget), shared between the desktop Header and MobileHeader's `actions` slot.
 *
 * Extracted so both bars stay in lockstep with ONE copy of the store wiring —
 * previously this logic lived only in Header.jsx, MobileHeader's `actions` prop
 * was never populated by anyone, and a phone had no way to reach either Inbox
 * or the AI Coach widget (Header itself is `display:none` below 767px).
 *
 * `variant` only ever picks CSS classes — the data, click handlers and
 * accessible names are identical on both bars, matching how the desktop
 * Header already behaved before this file existed (Header.test.jsx and
 * Header.focusTabs.test.jsx assert those names and keep passing unchanged).
 *
 * This button does NOT navigate to a full AI Coach page — there is no
 * dedicated destination left to navigate to. Home v2 is the single AI
 * surface now: `/ai-coach` redirects to `/` (App.jsx) and Home renders that
 * page's old thread view inline, so Sidebar deliberately carries no 'AI
 * Coach' row any more (see Sidebar.jsx's NAV_ITEMS comment — a second row
 * would just be a different door to the same screen). This button stays
 * what it always was: a one-click quick chat over whatever screen you're
 * already on, reachable from anywhere Home is not.
 */
export default function HeaderActions({ variant = 'desktop' }) {
    const navigate = useNavigate();
    const unreadInboxCount = useInboxStore(s => s.getUnreadCount());

    const isMobile = variant === 'mobile';
    const btnClass = isMobile ? 'mobile-header__action' : 'header__icon-btn';
    const badgeClass = isMobile ? 'mobile-header__badge' : 'header__inbox-badge';

    const handleAIChatClick = () => {
        useAICoachStore.getState().toggleWidget();
    };

    return (
        <>
            <button
                type="button"
                className={btnClass}
                onClick={() => navigate('/inbox')}
                title="Inbox"
                aria-label={unreadInboxCount > 0 ? `Inbox, ${unreadInboxCount} unread` : 'Inbox'}
            >
                <Inbox size={18} aria-hidden="true" />
                {unreadInboxCount > 0 && (
                    <span className={badgeClass} aria-hidden="true">{unreadInboxCount}</span>
                )}
            </button>

            <button
                type="button"
                className={btnClass}
                onClick={handleAIChatClick}
                title="AI Coach"
                aria-label="AI Coach"
            >
                <MessageSquare size={18} aria-hidden="true" />
            </button>
        </>
    );
}
